import React, { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Collapse,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonAddAlt1 as PersonAddIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarToday as CalendarIcon,
  PersonOutline as PersonOutlineIcon,
  ChatBubbleOutline as ChatIcon,
  CancelOutlined as CancelIcon,
  CheckCircleOutline as CheckCircleIcon,
  WorkspacePremium as MembershipIcon,
} from "@mui/icons-material";
import { useResolveChangeRequest } from "../../../hooks/useTrainers";
import { extractDomains } from "./InstituteRequestsTable";

// Helper to format ISO date string
const formatDateTime = (isoString) => {
  if (!isoString) return { date: "—", time: "" };
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return { date: String(isoString), time: "" };
    const date = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { date, time };
  } catch (e) {
    return { date: String(isoString), time: "" };
  }
};

const RequestDetailsModal = ({ open, onClose, request, onSuccess }) => {
  const [loadingAction, setLoadingAction] = useState(null);
  const [showRejectField, setShowRejectField] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const resolveMutation = useResolveChangeRequest();

  useEffect(() => {
    if (open) {
      setShowRejectField(false);
      setRejectionReason("");
      setRejectError("");
    }
  }, [open]);

  if (!request) return null;

  const requestId = request.request_id || request.id;
  const institute = request.institute || {};
  const assignedTrainer = request.assigned_trainer || null;
  const requestedTrainer = request.requested_trainer || request.reqTrainer || {};
  const assignedDomainsList = extractDomains(assignedTrainer?.domains);
  const requestedDomainsList = extractDomains(requestedTrainer?.domains || requestedTrainer?.domainsList);
  const { date: reqDate, time: reqTime } = formatDateTime(request.created_at || request.request_date);
  const isPending = String(request.status).toLowerCase() === "pending";

  const handleRejectClick = () => {
    if (!showRejectField) {
      setShowRejectField(true);
    } else {
      if (!rejectionReason.trim()) {
        setRejectError("Please enter a reason for rejecting this request.");
        return;
      }
      setRejectError("");
      handleAction("rejected", rejectionReason.trim());
    }
  };

  const handleCancelReject = () => {
    setShowRejectField(false);
    setRejectionReason("");
    setRejectError("");
  };

  const handleAction = async (actionType, reason = "") => {
    setLoadingAction(actionType);
    try {
      await resolveMutation.mutateAsync({
        requestId: requestId,
        action: actionType, // "approved" or "rejected"
        reason: reason || undefined,
      });

      setSnackbar({
        open: true,
        message: `Request ${actionType === "approved" ? "Approved" : "Rejected"} successfully!`,
        severity: actionType === "approved" ? "success" : "info",
      });

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        setLoadingAction(null);
        onClose();
      }, 700);
    } catch (error) {
      console.error("Error updating request status:", error);
      const detail = error?.response?.data?.detail;
      const errorMsg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Failed to update request status. Please try again.";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
      setLoadingAction(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            maxWidth: "680px",
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2.5, sm: 3 },
            pb: 2,
            borderBottom: "1px solid #F1F5F9",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                backgroundColor: "#EFF6FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonAddIcon sx={{ color: "#2563EB", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0F172A",
                  letterSpacing: "-0.01em",
                }}
              >
                Change Request #{requestId}
              </Typography>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12px", color: "#64748B" }}>
                Status:{" "}
                <strong style={{ textTransform: "capitalize", color: "#1E293B" }}>
                  {request.status}
                </strong>
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#94A3B8",
              "&:hover": { backgroundColor: "#F1F5F9", color: "#1E293B" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Modal Body */}
        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            py: 2.5,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            fontFamily: '"Poppins", sans-serif',
          }}
        >
          {/* Section 1: Institute Information */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceIcon sx={{ color: "#2563EB", fontSize: 18 }} />
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "14px", fontWeight: 700, color: "#1E293B" }}>
                Institute Information
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                p: 2.25,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* Row 1: Institute Name & Request Date */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {/* Left: Institute Name & Type */}
                <Box>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "15px", fontWeight: 700, color: "#0F172A" }}>
                    {institute.institute_name || request.institute_name || "—"}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12.5px", color: "#64748B", mt: 0.25 }}>
                    {institute.institute_type || request.location || "Educational Institute"}
                  </Typography>
                </Box>

                {/* Right: Request Date */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                  <CalendarIcon sx={{ color: "#2563EB", fontSize: 18, mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11.5px", fontWeight: 500, color: "#64748B" }}>
                      Request Date
                    </Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
                      {reqDate} {reqTime ? `, ${reqTime}` : ""}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Row 2: Membership Details */}
              {(institute.membership_name || institute.expire_date) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1.75,
                    borderTop: "1px solid #F1F5F9",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MembershipIcon sx={{ color: "#D97706", fontSize: 18 }} />
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12.5px", color: "#475569" }}>
                      Membership:{" "}
                      <strong style={{ color: "#0F172A" }}>
                        {institute.membership_name || "Standard"}
                      </strong>
                    </Typography>
                  </Box>
                  {institute.expire_date && (
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12px", color: "#64748B" }}>
                      Expires: {formatDateTime(institute.expire_date).date}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Section 2: Assigned & Requested Trainer Details */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonOutlineIcon sx={{ color: "#2563EB", fontSize: 20 }} />
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "14px", fontWeight: 700, color: "#1E293B" }}>
                Trainer Details
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {/* Currently Assigned Trainer Card */}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "12px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12px", fontWeight: 600, color: "#64748B" }}>
                    Currently Assigned
                  </Typography>
                  <Chip
                    label="Current"
                    size="small"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      backgroundColor: "#F1F5F9",
                      color: "#475569",
                      fontSize: "10.5px",
                      fontWeight: 600,
                      height: "20px",
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: "14.5px",
                      fontWeight: 700,
                      color: assignedTrainer?.trainer_name ? "#0F172A" : "#94A3B8",
                    }}
                  >
                    {assignedTrainer?.trainer_name || "No Trainer Assigned"}
                  </Typography>
                </Box>

                {/* Domains */}
                <Box sx={{ pt: 1, borderTop: "1px solid #F1F5F9" }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11px", fontWeight: 500, color: "#94A3B8", mb: 0.75 }}>
                    Assigned Domains:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {assignedDomainsList.length > 0 ? (
                      assignedDomainsList.map((d, i) => (
                        <Chip
                          key={i}
                          label={d}
                          size="small"
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            backgroundColor: "#F1F5F9",
                            color: "#475569",
                            border: "1px solid #E2E8F0",
                            fontSize: "11px",
                            fontWeight: 500,
                            height: "22px",
                            borderRadius: "6px",
                          }}
                        />
                      ))
                    ) : (
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11.5px", color: "#94A3B8" }}>None</Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Requested Trainer Card */}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #BFDBFE",
                  borderRadius: "12px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12px", fontWeight: 600, color: "#2563EB" }}>
                    Requested Trainer
                  </Typography>
                  <Chip
                    label="Requested"
                    size="small"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      backgroundColor: "#EFF6FF",
                      color: "#2563EB",
                      fontSize: "10.5px",
                      fontWeight: 600,
                      height: "20px",
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "14.5px", fontWeight: 700, color: "#0F172A" }}>
                    {requestedTrainer.trainer_name || requestedTrainer.fullname || requestedTrainer.name || "—"}
                  </Typography>
                </Box>

                {/* Domains */}
                <Box sx={{ pt: 1, borderTop: "1px solid #F1F5F9" }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11px", fontWeight: 500, color: "#94A3B8", mb: 0.75 }}>
                    Specializations & Domains:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {requestedDomainsList.length > 0 ? (
                      requestedDomainsList.map((d, i) => (
                        <Chip
                          key={i}
                          label={d}
                          size="small"
                          sx={{
                            fontFamily: '"Poppins", sans-serif',
                            backgroundColor: "#EFF6FF",
                            color: "#2563EB",
                            border: "1px solid #DBEAFE",
                            fontSize: "11px",
                            fontWeight: 500,
                            height: "22px",
                            borderRadius: "6px",
                          }}
                        />
                      ))
                    ) : (
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "11.5px", color: "#94A3B8" }}>
                        No domains listed
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Section 3: Remark from SPOC */}
          {request.remark && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ChatIcon sx={{ color: "#2563EB", fontSize: 18 }} />
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "14px", fontWeight: 700, color: "#1E293B" }}>
                  Remark from SPOC
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#F0F7FF",
                  border: "1px solid #DBEAFE",
                  borderRadius: "10px",
                  p: 2,
                }}
              >
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13px", color: "#334155", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                  {request.remark}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Section 4: Admin Action Reason (if already actioned) */}
          {(request.admin_reason || request.reason) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13px", fontWeight: 700, color: "#991B1B" }}>
                Admin Reason / Notes:
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#FEF2F2",
                  border: "1px solid #FCA5A5",
                  borderRadius: "10px",
                  p: 1.75,
                }}
              >
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "12.5px", color: "#7F1D1D" }}>
                  {request.admin_reason || request.reason}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Section 5: Rejection Reason Field (Opens when Reject is clicked) */}
          <Collapse in={showRejectField}>
            <Box
              sx={{
                backgroundColor: "#FEF2F2",
                border: "1px solid #FCA5A5",
                borderRadius: "12px",
                p: 2.25,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CancelIcon sx={{ color: "#DC2626", fontSize: 18 }} />
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: "13.5px", fontWeight: 700, color: "#991B1B" }}>
                  Reason for Rejection <span style={{ color: "#DC2626" }}>*</span>
                </Typography>
              </Box>

              <TextField
                multiline
                rows={3}
                placeholder="Please enter the reason for rejecting this trainer change request..."
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  if (rejectError) setRejectError("");
                }}
                error={Boolean(rejectError)}
                helperText={rejectError}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: '"Poppins", sans-serif',
                    backgroundColor: "#FFFFFF",
                    fontSize: "13px",
                    borderRadius: "8px",
                    "& fieldset": { borderColor: rejectError ? "#EF4444" : "#F87171" },
                    "&:hover fieldset": { borderColor: "#DC2626" },
                    "&.Mui-focused fieldset": { borderColor: "#DC2626" },
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: '"Poppins", sans-serif',
                  },
                }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Modal Footer Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            p: { xs: 2.5, sm: 3 },
            pt: 2,
            borderTop: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            flexShrink: 0,
          }}
        >
          {showRejectField ? (
            <>
              <Button
                variant="outlined"
                onClick={handleCancelReject}
                disabled={loadingAction !== null}
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  borderColor: "#E2E8F0",
                  color: "#64748B",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13.5px",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  "&:hover": {
                    borderColor: "#CBD5E1",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleRejectClick}
                disabled={loadingAction !== null}
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  backgroundColor: "#DC2626",
                  color: "#FFFFFF",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  px: 3.5,
                  py: 0.75,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#B91C1C",
                    boxShadow: "none",
                  },
                }}
              >
                {loadingAction === "rejected" ? (
                  <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
                ) : (
                  "Confirm Reject"
                )}
              </Button>
            </>
          ) : isPending ? (
            <>
              <Button
                variant="outlined"
                onClick={handleRejectClick}
                disabled={loadingAction !== null}
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  borderColor: "#EF4444",
                  color: "#EF4444",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  px: 3.5,
                  py: 0.75,
                  "&:hover": {
                    borderColor: "#DC2626",
                    backgroundColor: "#FEF2F2",
                  },
                }}
              >
                Reject
              </Button>

              <Button
                variant="contained"
                onClick={() => handleAction("approved")}
                disabled={loadingAction !== null}
                startIcon={
                  loadingAction === "approved" ? null : <CheckCircleIcon sx={{ fontSize: 18 }} />
                }
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  backgroundColor: "#2563EB",
                  color: "#FFFFFF",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  px: 3.5,
                  py: 0.75,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#1D4ED8",
                    boxShadow: "none",
                  },
                }}
              >
                {loadingAction === "approved" ? (
                  <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
                ) : (
                  "Approve"
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                borderColor: "#E2E8F0",
                color: "#64748B",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "13.5px",
                fontWeight: 500,
                px: 3,
                py: 0.75,
              }}
            >
              Close
            </Button>
          )}
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "8px", fontFamily: '"Poppins", sans-serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestDetailsModal;

